import { useCallback, useEffect, useLayoutEffect, useRef } from 'react';

const DEFAULT_DURATION_MS = 250;
const DEFAULT_SETTLE_MS = 0;
const SIZE_EPSILON_PX = 1;
const OUTER_PANEL_RESIZING_CLASS = 'floating-panel-shell--resizing';
const MEASURE_WIDTH_LOCK_CLASS = 'floating-panel-shell__content--width-locked';
const SIZE_MOTION_DURATION_VAR = '--animated-panel-size-duration';
const SIZE_MOTION_EASING_VAR = '--animated-panel-size-easing';

function parseStyleSize(value) {
  const parsed = Number.parseFloat(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function readNodeSize(node) {
  if (!node) {
    return { width: 0, height: 0 };
  }

  const rect = typeof node.getBoundingClientRect === 'function'
    ? node.getBoundingClientRect()
    : null;

  return {
    width: Math.round(rect?.width || node.offsetWidth || 0),
    height: Math.round(rect?.height || node.offsetHeight || 0),
  };
}

function normalizeObservedSize(entry, fallbackNode) {
  const borderBox = Array.isArray(entry?.borderBoxSize)
    ? entry.borderBoxSize[0]
    : entry?.borderBoxSize;

  if (
    borderBox
    && Number.isFinite(borderBox.inlineSize)
    && Number.isFinite(borderBox.blockSize)
  ) {
    return {
      width: Math.round(borderBox.inlineSize),
      height: Math.round(borderBox.blockSize),
    };
  }

  if (entry?.contentRect) {
    return {
      width: Math.round(entry.contentRect.width),
      height: Math.round(entry.contentRect.height),
    };
  }

  return readNodeSize(fallbackNode);
}

function sizesDiffer(previousSize, nextSize) {
  if (!previousSize || !nextSize) {
    return false;
  }

  return Math.abs(previousSize.width - nextSize.width) > SIZE_EPSILON_PX
    || Math.abs(previousSize.height - nextSize.height) > SIZE_EPSILON_PX;
}

function prefersReducedMotion() {
  return typeof window !== 'undefined'
    && typeof window.matchMedia === 'function'
    && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

function addSizeDelta(size, delta = { width: 0, height: 0 }) {
  return {
    width: Math.max(0, Math.round((size?.width || 0) + (delta?.width || 0))),
    height: Math.max(0, Math.round((size?.height || 0) + (delta?.height || 0))),
  };
}

function subtractSize(baseSize, childSize) {
  return {
    width: Math.max(0, Math.round((baseSize?.width || 0) - (childSize?.width || 0))),
    height: Math.max(0, Math.round((baseSize?.height || 0) - (childSize?.height || 0))),
  };
}

function getActiveMeasureNode(measureRef, fallbackNode) {
  return measureRef.current || fallbackNode;
}

function measureNaturalTargetSizeUnderLock(node, lockedSize, beforeMeasure) {
  if (!node) {
    return lockedSize || { width: 0, height: 0 };
  }

  const previousTransition = node.style.transition;
  node.style.transition = 'none';
  node.style.width = '';
  node.style.height = '';

  // Converge dependent layout vars HERE — after the imposed size is off, before the read.
  // Order is the whole point: run it while the shell is still pinned to its animating size
  // and the metrics converge against that transient geometry instead of the real resting
  // layout, which lands the target ~17px high. Measured, not reasoned.
  beforeMeasure?.();

  const naturalSize = readNodeSize(node);

  node.style.width = `${Math.max(0, Math.round(lockedSize.width || 0))}px`;
  node.style.height = `${Math.max(0, Math.round(lockedSize.height || 0))}px`;
  void node.offsetWidth;
  node.style.transition = previousTransition;

  return naturalSize.width > 0 && naturalSize.height > 0 ? naturalSize : lockedSize;
}

function isInsideAncestorPanelResize(node) {
  return Boolean(node?.parentElement?.closest?.(`.${OUTER_PANEL_RESIZING_CLASS}`));
}

function normalizeSizeMotion(motion, fallbackDurationMs) {
  const motionDuration = Number.isFinite(motion?.durationMs)
    ? Math.max(0, Math.round(motion.durationMs))
    : fallbackDurationMs;
  const motionEasing = typeof motion?.easing === 'string' && motion.easing.trim()
    ? motion.easing.trim()
    : null;

  return {
    durationMs: motionDuration,
    easing: motionEasing,
  };
}

function writeSizeMotionVars(node, motion) {
  if (!node) return;

  node.style.setProperty(SIZE_MOTION_DURATION_VAR, `${motion.durationMs}ms`);
  if (motion.easing) {
    node.style.setProperty(SIZE_MOTION_EASING_VAR, motion.easing);
  } else {
    node.style.removeProperty(SIZE_MOTION_EASING_VAR);
  }
}

function clearSizeMotionVars(node) {
  if (!node) return;

  node.style.removeProperty(SIZE_MOTION_DURATION_VAR);
  node.style.removeProperty(SIZE_MOTION_EASING_VAR);
}

function resetMeasureWidthLock(node) {
  if (!node) return;

  node.classList.remove(MEASURE_WIDTH_LOCK_CLASS);
  node.style.width = '';
}

function prepareMeasureWidthLock(node, startSize, nextSize, frameDelta) {
  if (!node || !startSize || !nextSize) return;
  if (nextSize.width >= startSize.width - SIZE_EPSILON_PX) return;

  const lockedWidth = Math.max(0, Math.round(startSize.width - (frameDelta?.width || 0)));
  if (lockedWidth <= 0) return;

  node.classList.add(MEASURE_WIDTH_LOCK_CLASS);
  node.style.width = `${lockedWidth}px`;
}

export function useAnimatedPanelSize(
  targetRef,
  {
    enabled = true,
    measureRef = targetRef,
    className = OUTER_PANEL_RESIZING_CLASS,
    durationMs = DEFAULT_DURATION_MS,
    resolveMotion = null,
    settleMs = DEFAULT_SETTLE_MS,
    forceAnimation = false,
    onSettled = null,
    // Called when a morph ends WITHOUT settling — disable, hidden, reduced-motion, or an
    // unmount mid-flight. With one reveal owner, every exit from that owner has to be
    // accounted for: a consumer that hid its row on capture and waits for `onSettled` to
    // show it again is otherwise stranded invisible, permanently, with nothing to correct
    // it. An orphaned reveal is worse than an early one — early is a flash, orphaned is
    // controls that never come back.
    onCanceled = null,
    // Run immediately before every natural-size read. The composer uses it to converge its
    // action-rail metrics, whose vars feed the very layout being measured. Without it the
    // first target is taken while those vars are still mid-convergence, and every later
    // correction is another morph. See COMPOSER-ONE-CLOCK-SPEC.md.
    beforeMeasure = null,
    lockMeasureWidthOnShrink = false,
  } = {},
) {
  const previousSizeRef = useRef(null);
  const frameDeltaRef = useRef({ width: 0, height: 0 });
  const forceAnimationRef = useRef(forceAnimation);
  const onSettledRef = useRef(onSettled);
  const beforeMeasureRef = useRef(beforeMeasure);
  const onCanceledRef = useRef(onCanceled);
  const observerRef = useRef(null);
  const observedMeasureNodeRef = useRef(null);
  const reobserveFrameRef = useRef(null);
  const frameRef = useRef(null);
  const finishTimerRef = useRef(null);
  const settleTimerRef = useRef(null);
  const isSettlingRef = useRef(false);

  const clearAnimationFrame = useCallback(() => {
    if (frameRef.current != null) {
      cancelAnimationFrame(frameRef.current);
      frameRef.current = null;
    }
  }, []);

  const clearFinishTimer = useCallback(() => {
    if (finishTimerRef.current != null) {
      clearTimeout(finishTimerRef.current);
      finishTimerRef.current = null;
    }
  }, []);

  const clearSettleTimer = useCallback(() => {
    if (settleTimerRef.current != null) {
      clearTimeout(settleTimerRef.current);
      settleTimerRef.current = null;
    }
  }, []);

  const clearDeferredReobserve = useCallback(() => {
    if (reobserveFrameRef.current != null) {
      cancelAnimationFrame(reobserveFrameRef.current);
      reobserveFrameRef.current = null;
    }
  }, []);

  const runWithObservationPaused = useCallback((callback) => {
    const observer = observerRef.current;
    const measureNode = observedMeasureNodeRef.current;
    clearDeferredReobserve();

    if (!observer || !measureNode) {
      return callback();
    }

    observer.unobserve(measureNode);
    try {
      return callback();
    } finally {
      clearDeferredReobserve();
      reobserveFrameRef.current = requestAnimationFrame(() => {
        reobserveFrameRef.current = null;
        if (observerRef.current === observer && observedMeasureNodeRef.current === measureNode) {
          observer.observe(measureNode);
        }
      });
    }
  }, [clearDeferredReobserve]);

  const resetNodeStyles = useCallback(() => {
    const node = targetRef.current;
    resetMeasureWidthLock(measureRef.current);
    if (!node) {
      return;
    }

    node.classList.remove(className);
    node.style.width = '';
    node.style.height = '';
    clearSizeMotionVars(node);
  }, [className, measureRef, targetRef]);

  const syncFromDom = useCallback(() => {
    const activeTargetNode = targetRef.current;
    const activeMeasureNode = getActiveMeasureNode(measureRef, activeTargetNode);
    if (!activeTargetNode) {
      return;
    }

    const nextTargetSize = readNodeSize(activeTargetNode);
    const nextMeasureSize = readNodeSize(activeMeasureNode);
    previousSizeRef.current = nextTargetSize;
    frameDeltaRef.current = subtractSize(nextTargetSize, nextMeasureSize);
  }, [measureRef, targetRef]);

  const resolveSizeMotion = useCallback((startSize, nextSize) => {
    const resolvedMotion = typeof resolveMotion === 'function'
      ? resolveMotion({ startSize, nextSize, durationMs })
      : null;
    return normalizeSizeMotion(resolvedMotion, durationMs);
  }, [durationMs, resolveMotion]);

  const finishAnimation = useCallback((options = {}) => {
    const { notifySettled = true } = options;
    clearAnimationFrame();
    clearFinishTimer();
    runWithObservationPaused(() => {
      resetNodeStyles();
      // Reconcile once, with the imposed size off. Metric writers are frozen for the whole
      // morph, so anything that genuinely changed the content meanwhile — a prompt edit, a
      // reference added, async layout — was dropped rather than queued. This used to be
      // caught by resyncing metrics on every settle, which is the replay loop that made one
      // resize into four; the honest replacement is to converge ONCE here, after the reset
      // and before anyone reads the settled size, rather than to keep a channel that fires
      // per settle. Without this, deleting the replay leaves those changes stale until an
      // unrelated later resize.
      beforeMeasureRef.current?.();
      if (notifySettled) {
        onSettledRef.current?.();
      } else {
        // The one exit that skips the reveal owner. Resolve the pending reveal rather than
        // leaving it hanging — see `onCanceled`.
        onCanceledRef.current?.();
      }
      syncFromDom();
    });
  }, [
    clearAnimationFrame,
    clearFinishTimer,
    resetNodeStyles,
    runWithObservationPaused,
    syncFromDom,
  ]);

  const lockCurrentSizeForNextAnimation = useCallback(() => {
    const activeNode = targetRef.current;
    const activeMeasureNode = getActiveMeasureNode(measureRef, activeNode);
    if (!activeNode || !activeMeasureNode || prefersReducedMotion()) {
      return;
    }

    const computedStyle = typeof window.getComputedStyle === 'function'
      ? window.getComputedStyle(activeNode)
      : null;
    const domSize = readNodeSize(activeNode);
    const currentSize = {
      width: Math.round(parseStyleSize(computedStyle?.width) || domSize.width || 0),
      height: Math.round(parseStyleSize(computedStyle?.height) || domSize.height || 0),
    };

    if (currentSize.width <= 0 || currentSize.height <= 0) {
      return;
    }

    const currentMeasureSize = readNodeSize(activeMeasureNode);
    frameDeltaRef.current = subtractSize(currentSize, currentMeasureSize);

    clearAnimationFrame();
    clearFinishTimer();

    const previousInlineTransition = activeNode.style.transition;
    activeNode.classList.add(className);
    activeNode.style.transition = 'none';
    activeNode.style.width = `${currentSize.width}px`;
    activeNode.style.height = `${currentSize.height}px`;
    void activeNode.offsetWidth;
    activeNode.style.transition = previousInlineTransition;
    previousSizeRef.current = currentSize;
  }, [
    className,
    clearAnimationFrame,
    clearFinishTimer,
    measureRef,
    targetRef,
  ]);

  const animateToMeasuredSize = useCallback(() => {
    const activeNode = targetRef.current;
    if (!activeNode) {
      return;
    }

    if (prefersReducedMotion()) {
      finishAnimation();
      return;
    }

    const currentSize = previousSizeRef.current || readNodeSize(activeNode);
    if (currentSize.width <= 0 || currentSize.height <= 0) {
      finishAnimation();
      return;
    }

    clearAnimationFrame();
    clearFinishTimer();

    const nextSize = runWithObservationPaused(() => {
      // Converge dependent layout vars FIRST, inside the paused window, so the natural size
      // read below is the settled one rather than the first of several.
      return measureNaturalTargetSizeUnderLock(activeNode, currentSize, beforeMeasureRef.current);
    });
    if (!sizesDiffer(currentSize, nextSize)) {
      finishAnimation();
      return;
    }

    const motion = resolveSizeMotion(currentSize, nextSize);
    if (lockMeasureWidthOnShrink) {
      prepareMeasureWidthLock(measureRef.current, currentSize, nextSize, frameDeltaRef.current);
    }
    writeSizeMotionVars(activeNode, motion);
    // The animated node is the only geometry owner. The positioned ancestor is
    // `width: fit-content`, so it tracks this width every frame and its
    // `translateX(-50%)` recentres the panel natively — no JS x-compensation.
    activeNode.style.width = `${nextSize.width}px`;
    activeNode.style.height = `${nextSize.height}px`;
    finishTimerRef.current = window.setTimeout(finishAnimation, motion.durationMs + 80);
    previousSizeRef.current = nextSize;
  }, [
    clearAnimationFrame,
    clearFinishTimer,
    finishAnimation,
    lockMeasureWidthOnShrink,
    measureRef,
    resolveSizeMotion,
    runWithObservationPaused,
    targetRef,
  ]);

  useEffect(() => {
    forceAnimationRef.current = forceAnimation;
  }, [forceAnimation]);

  useEffect(() => {
    onSettledRef.current = onSettled;
    beforeMeasureRef.current = beforeMeasure;
    onCanceledRef.current = onCanceled;
  }, [onSettled, beforeMeasure, onCanceled]);

  useLayoutEffect(() => {
    const targetNode = targetRef.current;
    const measureNode = measureRef.current || targetNode;
    if (!targetNode || !measureNode) {
      return undefined;
    }

    const stopAllMotion = () => {
      clearSettleTimer();
      isSettlingRef.current = false;
      finishAnimation({ notifySettled: false });
    };

    syncFromDom();

    if (!enabled || typeof ResizeObserver !== 'function' || prefersReducedMotion()) {
      stopAllMotion();
      return undefined;
    }

    if (settleMs > 0) {
      isSettlingRef.current = true;
      clearSettleTimer();
      settleTimerRef.current = window.setTimeout(() => {
        isSettlingRef.current = false;
        settleTimerRef.current = null;
        syncFromDom();
      }, settleMs);
    } else {
      isSettlingRef.current = false;
    }

    const animateToSize = (nextSize) => {
      const activeNode = targetRef.current;
      if (!activeNode) {
        return;
      }

      const computedStyle = typeof window.getComputedStyle === 'function'
        ? window.getComputedStyle(activeNode)
        : null;
      const previousSize = previousSizeRef.current || nextSize;
      const isRetargetingActiveAnimation = activeNode.classList.contains(className);
      const startSize = {
        width: isRetargetingActiveAnimation
          ? parseStyleSize(computedStyle?.width) || previousSize.width || nextSize.width
          : previousSize.width || parseStyleSize(computedStyle?.width) || nextSize.width,
        height: isRetargetingActiveAnimation
          ? parseStyleSize(computedStyle?.height) || previousSize.height || nextSize.height
          : previousSize.height || parseStyleSize(computedStyle?.height) || nextSize.height,
      };

      if (!sizesDiffer(startSize, nextSize)) {
        previousSizeRef.current = nextSize;
        if (!isRetargetingActiveAnimation) {
          syncFromDom();
          finishAnimation();
        }
        return;
      }

      clearAnimationFrame();
      clearFinishTimer();

      const motion = resolveSizeMotion(startSize, nextSize);
      const previousInlineTransition = activeNode.style.transition;
      activeNode.classList.add(className);
      activeNode.style.transition = 'none';
      writeSizeMotionVars(activeNode, motion);
      activeNode.style.width = `${Math.max(0, Math.round(startSize.width))}px`;
      activeNode.style.height = `${Math.max(0, Math.round(startSize.height))}px`;
      void activeNode.offsetWidth;
      activeNode.style.transition = previousInlineTransition;

      frameRef.current = requestAnimationFrame(() => {
        const nextNode = targetRef.current;
        if (!nextNode) {
          return;
        }

        nextNode.style.width = `${nextSize.width}px`;
        nextNode.style.height = `${nextSize.height}px`;
      });

      finishTimerRef.current = window.setTimeout(finishAnimation, motion.durationMs + 80);
      previousSizeRef.current = nextSize;
    };

    let observer;

    observer = new ResizeObserver((entries) => {
      const activeNode = targetRef.current;
      const activeMeasureNode = measureRef.current || activeNode;
      if (!activeNode || !activeMeasureNode) {
        return;
      }

      const matchingEntry = entries.find((entry) => entry.target === activeMeasureNode) || entries[0];
      const observedSize = normalizeObservedSize(matchingEntry, activeMeasureNode);
      const nextSize = addSizeDelta(observedSize, frameDeltaRef.current);
      const previousSize = previousSizeRef.current;

      if (!previousSize) {
        previousSizeRef.current = nextSize;
        return;
      }

      if (!sizesDiffer(previousSize, nextSize)) {
        previousSizeRef.current = nextSize;
        return;
      }

      if (isSettlingRef.current && !forceAnimationRef.current) {
        previousSizeRef.current = nextSize;
        return;
      }

      if (isInsideAncestorPanelResize(activeNode)) {
        previousSizeRef.current = nextSize;
        return;
      }

      runWithObservationPaused(() => animateToSize(nextSize));
    });

    observerRef.current = observer;
    observedMeasureNodeRef.current = measureNode;
    observer.observe(measureNode);

    return () => {
      clearDeferredReobserve();
      observer.disconnect();
      if (observerRef.current === observer) {
        observerRef.current = null;
      }
      if (observedMeasureNodeRef.current === measureNode) {
        observedMeasureNodeRef.current = null;
      }
      stopAllMotion();
    };
  }, [
    className,
    durationMs,
    enabled,
    measureRef,
    settleMs,
    targetRef,
    clearDeferredReobserve,
    clearAnimationFrame,
    clearFinishTimer,
    clearSettleTimer,
    finishAnimation,
    resolveSizeMotion,
    runWithObservationPaused,
    syncFromDom,
  ]);

  return {
    animateToMeasuredSize,
    lockCurrentSizeForNextAnimation,
  };
}

export default useAnimatedPanelSize;
